import { type FC, useState } from 'react';
import { graphql, useMutation } from 'react-relay';
import { FormattedMessage, defineMessages, useIntl } from 'dibs-react-intl';
import CircleCheck from 'dibs-icons/exports/legacy/CircleCheck';
import { ModalContainer } from 'dibs-elements/exports/ModalContainer';
import { ModalBody } from 'dibs-elements/exports/ModalBody';
import { ModalError } from 'dibs-elements/exports/ModalError';
import { BarHeader } from 'dibs-elements/exports/BarHeader';
import { TextArea } from 'dibs-elements/exports/TextArea';
import { Button } from 'dibs-elements/exports/Button';
import { ModalCloseButton } from 'dibs-elements/exports/ModalCloseButton';
import { ModalSpinner } from 'dibs-elements/exports/ModalSpinner';
import { type NftPublicProfileReportModalMutation } from './__generated__/NftPublicProfileReportModalMutation.graphql';

import styles from './NftPublicProfileReportModal.scss';

const messages = defineMessages({
    reasonPlaceholder: {
        id: 'nft.NftPublicProfileReportModal.reasonPlaceholder',
        defaultMessage: 'Add a note for 1stDibs (optional)',
    },
});

export const NftPublicProfileReportModal: FC<{
    username: string;
    isOpen?: boolean;
    onClose: () => void;
}> = ({ isOpen, onClose, username }) => {
    const intl = useIntl();
    const [error, setError] = useState(false);
    const [state, setState] = useState<'PENDING' | 'SAVING' | 'SAVED'>('PENDING');
    const [additionalInfo, setAdditionalInfo] = useState('');

    const [commitUpdate] = useMutation<NftPublicProfileReportModalMutation>(
        graphql`
            mutation NftPublicProfileReportModalMutation($input: PublicProfileReportInput!) {
                publicProfileReport(input: $input) {
                    __typename
                }
            }
        `
    );

    const handleClick = (): void => {
        setState('SAVING');
        commitUpdate({
            variables: {
                input: {
                    username,
                    additionalInfo,
                },
            },
            onCompleted() {
                setState('SAVED');
            },
            onError() {
                setState('PENDING');
                setError(true);
            },
        });
    };

    return (
        <ModalContainer
            modalSize="default"
            isOpen={!!isOpen}
            dataTn="public-profile-report-modal"
            onClose={onClose}
        >
            <ModalCloseButton onClick={onClose} disabled={state === 'SAVING'} />
            {['SAVING', 'PENDING'].includes(state) ? (
                <>
                    <BarHeader
                        title={
                            <FormattedMessage
                                id="nft.NftPublicProfileReportModal.reportProfile"
                                defaultMessage="Report Profile"
                            />
                        }
                    />
                    <ModalBody>
                        <p className={styles.modalBody}>
                            <FormattedMessage
                                id="nft.NftPublicProfileReportModal.body"
                                defaultMessage="To report this profile, click the button below. You may also add an optional note as to why you believe the profile is inappropriate."
                            />
                        </p>
                        <div className={styles.inputWrapper}>
                            <TextArea
                                dataTn="public-profile-report-modal-reason"
                                value={additionalInfo}
                                onChange={e => setAdditionalInfo(e.target.value)}
                                placeholder={intl.formatMessage(messages.reasonPlaceholder)}
                            />
                        </div>
                        {!!error && (
                            <ModalError>
                                <FormattedMessage
                                    id="nft.NftPublicProfileReportModal.error"
                                    defaultMessage="There was an error reporting this profile."
                                />
                            </ModalError>
                        )}
                        <Button fullWidth onClick={handleClick}>
                            <FormattedMessage
                                id="nft.NftPublicProfileReportModal.report"
                                defaultMessage="REPORT"
                            />
                        </Button>
                        <ModalSpinner isOpen={state === 'SAVING'} />
                    </ModalBody>
                </>
            ) : (
                <>
                    <BarHeader
                        title={
                            <>
                                <div>
                                    <CircleCheck className={styles.circleCheck} />
                                </div>
                                <FormattedMessage
                                    id="nft.NftPublicProfileReportModal.profileReported"
                                    defaultMessage="Profile Reported"
                                />
                            </>
                        }
                    />

                    <ModalBody>
                        <p className={styles.modalBody}>
                            <FormattedMessage
                                id="nft.NftPublicProfileReportModal.review"
                                defaultMessage="You have successfully reported this profile to 1stDibs. Our team will review the profile and determine next steps."
                            />
                        </p>
                    </ModalBody>
                </>
            )}
        </ModalContainer>
    );
};
